import { useState, useRef } from 'react';
import { useParams, Link } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Pagination } from '../../components/ui/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { ArrowLeft, Search, Trash2, RefreshCw, Users } from 'lucide-react';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';

const PAGE_SIZE = 20;

export function SmartCorrectVendorsPage() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleSearchChange(value: string) {
    setSearch(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  }

  const { data: service } = useQuery({
    queryKey: ['smart-correct', id],
    queryFn: () =>
      api
        .get('/api/smart-correct/get', { params: { service_instance_id: id } })
        .then((r) => r.data),
    enabled: !!id,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['smart-correct-vendors', id, page, debouncedSearch],
    queryFn: () =>
      api
        .get(`/api/smart-correct/${id}/vendors`, {
          params: {
            offset: (page - 1) * PAGE_SIZE,
            limit: PAGE_SIZE,
            search: debouncedSearch || undefined,
          },
        })
        .then((r) => r.data),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: (vendorId: string) =>
      api.delete(`/api/smart-correct/${id}/vendors/${vendorId}`),
    onSuccess: () => {
      toast.success('Vendor deleted');
      queryClient.invalidateQueries({
        queryKey: ['smart-correct-vendors', id],
      });
    },
    onError: () => toast.error('Failed to delete vendor'),
  });

  const syncMutation = useMutation({
    mutationFn: () => api.post(`/api/smart-correct/${id}/sync`),
    onSuccess: () => {
      toast.success('Catalog sync triggered');
      queryClient.invalidateQueries({
        queryKey: ['smart-correct-vendors', id],
      });
    },
    onError: () => toast.error('Failed to trigger sync'),
  });

  const s = service?.query?.[0] ?? service;
  const vendors = data?.query?.data ?? [];
  const total = data?.query?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link to={`/smart-correct/${id}`}>
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6 text-green-600" />
            Confirmed Vendors
          </h1>
          <p className="text-gray-500 text-sm">
            {s?.service_title ?? 'Smart Correct'} — Confirmed vendor list
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${syncMutation.isPending ? 'animate-spin' : ''}`}
          />
          {syncMutation.isPending ? 'Syncing...' : 'Sync Catalog'}
        </Button>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search vendors..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="pl-10 max-w-sm"
        />
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-500">
          Loading vendors...
        </div>
      ) : vendors.length === 0 ? (
        <Card className="p-12 text-center">
          <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No confirmed vendors</h3>
          <p className="text-gray-500">
            Vendors will appear here once they are confirmed through the Smart
            Correct matching process.
          </p>
        </Card>
      ) : (
        <>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vendor Name</TableHead>
                  <TableHead>Vendor ID</TableHead>
                  <TableHead>Street</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Tax ID</TableHead>
                  <TableHead>Last Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vendors.map((vendor: any) => (
                  <TableRow key={vendor.id || vendor.vendor_id}>
                    <TableCell className="font-medium">
                      {vendor.name}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {vendor.vendor_id}
                    </TableCell>
                    <TableCell>{vendor.street || '—'}</TableCell>
                    <TableCell>{vendor.city || '—'}</TableCell>
                    <TableCell>{vendor.country || '—'}</TableCell>
                    <TableCell>{vendor.tax_id || '—'}</TableCell>
                    <TableCell className="text-gray-500">
                      {formatDate(vendor.updated_at || vendor.last_updated)}
                    </TableCell>
                    <TableCell className="text-right">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" title="Delete">
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              Delete {vendor.name}?
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              This will remove this vendor from the confirmed
                              list. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() =>
                                deleteMutation.mutate(
                                  vendor.id || vendor.vendor_id
                                )
                              }
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          {totalPages > 1 && (
            <div className="mt-4">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}